/**
 * Facebook Pixel Adapter
 *
 * Adapts standardized event data into Facebook Pixel format.
 * Handles both browser pixel (fbq) and CAPI (server-to-server) data structures.
 *
 * @since 1.51.0
 */

(function (wpm, $, undefined) {

	/**
	 * Facebook Pixel Data Adapter
	 */
	const FacebookAdapter = {

		/**
		 * Main adapt method - routes to specific event adapters
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (add_to_cart, view_item, etc.)
		 * @param {object} context - Event context (user, session, page data)
		 * @returns {object|null} Facebook-formatted data or null if pixel inactive
		 */
		adapt(eventData, eventName, context) {
			// Check if Facebook pixel is configured (has pixel_id)
			// Don't check 'loaded' status - pixel might not be loaded yet when event fires
			// The event listener will check canNotFireFbq() before actually firing
			if (!wpmDataLayer?.pixels?.facebook?.pixel_id) {
				return null;
			}

			// Map standardized event names to adapter methods
			const eventMap = {
				"page_view"       : this.adaptPageView,
				"add_to_cart"     : this.adaptAddToCart,
				"view_item"       : this.adaptViewItem,
				"begin_checkout"  : this.adaptBeginCheckout,
				"add_payment_info": this.adaptAddPaymentInfo,
				"add_to_wishlist" : this.adaptAddToWishlist,
				"search"          : this.adaptSearch,
				"view_category"   : this.adaptViewCategory,
				"purchase"        : this.adaptPurchase,
				"place_order"     : this.adaptPlaceOrder,
			};
			const adapter  = eventMap[eventName];

			if (!adapter) {
				pmw.console.log(`No Facebook adapter for event: ${eventName}`);
				return null;
			}

			try {
				return adapter.call(this, eventData, context);
			} catch (e) {
				pmw.console.error(`Facebook adapter error for ${eventName}:`, e);
				return null;
			}
		},

		/**
		 * Get base event data structure
		 * Common fields for all Facebook events
		 */
		getBaseData(context) {
			const baseData = {
				event_id        : wpm.getFbRandomEventId(),
				user_data       : wpm.getFbUserData(),
				event_source_url: context.url,
			};

			// Add referrer if available
			if (context.referrer) {
				baseData.referrer_url = context.referrer;
			}

			return baseData;
		},

		/**
		 * Adapt PageView event
		 */
		adaptPageView(eventData, context) {
			return {
				event_name: "PageView",
				...this.getBaseData(context),
				custom_data: {},
			};
		},

		/**
		 * Adapt AddToCart event
		 */
		adaptAddToCart(eventData, context) {
			const product = eventData.product;

			return {
				event_name: "AddToCart",
				...this.getBaseData(context),
				custom_data: wpm.fbGetProductDataForCapiEvent(product),
			};
		},

		/**
		 * Adapt ViewContent (view_item) event
		 */
		adaptViewItem(eventData, context) {
			const product = eventData.product;

			const data = {
				event_name: "ViewContent",
				...this.getBaseData(context),
				custom_data: {},
			};

			// Add product data if product is provided
			if (product) {
				data.custom_data = wpm.fbGetProductDataForCapiEvent(product);
			}

			return data;
		},

		/**
		 * Adapt InitiateCheckout (begin_checkout) event
		 */
		adaptBeginCheckout(eventData, context) {
			const data = {
				event_name: "InitiateCheckout",
				...this.getBaseData(context),
				custom_data: {},
			};

			// Add cart data if available
			if (wpmDataLayer?.cart && !jQuery.isEmptyObject(wpmDataLayer.cart)) {
				data.custom_data = {
					content_type: "product",
					content_ids : wpm.fbGetContentIdsFromCart(),
					value       : wpm.getCartValue(),
					currency    : wpmDataLayer.shop.currency,
				};
			}

			return data;
		},

		/**
		 * Adapt AddPaymentInfo event
		 */
		adaptAddPaymentInfo(eventData, context) {
			return {
				event_name: "AddPaymentInfo",
				...this.getBaseData(context),
				custom_data: {},
			};
		},

		/**
		 * Adapt AddToWishlist event
		 */
		adaptAddToWishlist(eventData, context) {
			const product = eventData.product;

			return {
				event_name: "AddToWishlist",
				...this.getBaseData(context),
				custom_data: wpm.fbGetProductDataForCapiEvent(product),
			};
		},

		/**
		 * Adapt Search event
		 */
		adaptSearch(eventData, context) {
			return {
				event_name: "Search",
				...this.getBaseData(context),
				custom_data: {
					search_string: wpm.getSearchTermFromUrl(),
				},
			};
		},

		/**
		 * Adapt ViewCategory event
		 * Note: This is a custom event for Facebook
		 */
		adaptViewCategory(eventData, context) {
			return {
				event_name: "ViewCategory",
				...this.getBaseData(context),
				custom_data: {},
			};
		},

		/**
		 * Adapt Purchase event
		 */
		adaptPurchase(eventData, context) {
			if (!wpmDataLayer?.order) {
				pmw.console.error("Order data not available for Facebook Purchase event");
				return null;
			}

			const baseData    = this.getBaseData(context);
			// Use order ID for purchase event to match server-side event ID
			baseData.event_id = wpmDataLayer.order.id.toString();

			return {
				event_name: "Purchase",
				...baseData,
				custom_data: {
					value       : wpmDataLayer.order.value.marketing,
					currency    : wpmDataLayer.order.currency,
					content_type: "product",
					content_ids : wpm.facebookContentIds(),
				},
			};
		}, /**
		 * Adapt PlaceOrder event
		 * Note: This might be a custom event depending on your needs
		 */
		adaptPlaceOrder(eventData, context) {
			return {
				event_name: "PlaceOrder",
				...this.getBaseData(context),
				custom_data: {},
			};
		},
	};

	// Register the adapter with S2S capabilities
	wpm.registerAdapter("facebook", FacebookAdapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		isActive                  : () => wpmDataLayer?.pixels?.facebook?.loaded === true,
	});

}(window.wpm = window.wpm || {}, jQuery));
